import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../card';
import { Input } from '../../input';
import { Button } from '../../button/button';
import { AlertTriangle, BarChart2, User, Bot, Send } from 'lucide-react';
import { Markdown } from '../../markdown';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../tabs';
import { ConversationProps } from './types';
import { useConversationAutoScroll } from '../../../../hooks/useConversationAutoScroll';
import { useSimulationStore } from '../../../../stores/simulationStore';
import { usePersonas } from '../../../../contexts/PersonaContext';
import { useTutorialStore } from '../../../../stores/tutorialStore';
import { SPEND_LIMIT_REACHED } from '../../../../lib/managed-agents/api';
import { ENDPOINT_ERROR } from '../../../../lib/external-agents/api';
import { RaiseSpendLimit } from './raise-spend-limit';

export function Conversation({
  canStartConversation,
  userMessage,
  setUserMessage,
  handleSendMessage,
  isSendingMessage,
  typingRole,
  failures,
  onRaiseLimitAndContinue,
  isImportedAgent,
}: ConversationProps): JSX.Element {
  const {
    isSimulating,
    selectedPersonas,
    selectedPersonaTab,
    personaMessages,
    personaSimulations,
    setSimulationState,
  } = useSimulationStore();

  const { personas } = usePersonas();
  const navigate = useNavigate();
  const { markMilestoneComplete } = useTutorialStore();

  const { conversationScrollRefs } = useConversationAutoScroll({
    selectedPersonaTab,
    personaMessages,
    isSimulating,
  });

  const activePersonaId = selectedPersonaTab || selectedPersonas[0];
  const activeSimulationId = activePersonaId
    ? personaSimulations[activePersonaId]
    : null;
  const canEvaluateConversation =
    !!activeSimulationId &&
    !failures[activeSimulationId] &&
    !isSimulating &&
    (activePersonaId
      ? (personaMessages[activePersonaId] || []).length > 0
      : false);

  const handleEvaluateConversation = () => {
    if (!activeSimulationId) return;
    markMilestoneComplete('evaluationOpened');
    navigate(`/evaluations?simulationId=${activeSimulationId}`);
  };

  return (
    <Card
      id="chat-area"
      className="rounded-[10px] border border-solid border-[#00000020] bg-prompt-safecomfantasy shadow-[0px_2px_4px_#0000001a]"
      data-onboarding="simulation-wait"
    >
      <CardHeader className="flex flex-row items-center justify-between gap-3">
        <CardTitle className="font-prompt-safe-com-semantic-heading-5 text-prompt-safecomvulcan">
          Conversation
        </CardTitle>
        {canEvaluateConversation && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            data-onboarding="evaluate-conversation"
            onClick={handleEvaluateConversation}
            className="border-prompt-safecomoutrageous-orange bg-prompt-safecomwhite text-prompt-safecomoutrageous-orange hover:bg-prompt-safecomfantasy"
            tooltipContent="Evaluate this conversation"
          >
            <BarChart2 className="h-4 w-4" />
            Evaluate conversation
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {selectedPersonas.length > 0 ? (
          <Tabs
            value={selectedPersonaTab || selectedPersonas[0]}
            onValueChange={(value) => {
              const simulationId = personaSimulations[value] ?? null;
              setSimulationState({
                selectedPersonaTab: value,
                selectedSimulation: simulationId,
              });
              if (simulationId) {
                navigate(`/conversation-simulation/${simulationId}`, {
                  replace: true,
                });
              } else {
                navigate('/conversation-simulation', { replace: true });
              }
            }}
          >
            <TabsList className="flex h-[unset] max-h-20 w-full flex-wrap justify-start gap-1 overflow-y-auto">
              {selectedPersonas.map((personaId) => {
                const persona = personas.find((p) => p.id === personaId);
                return (
                  <TabsTrigger key={personaId} value={personaId}>
                    {persona?.name || 'Unknown Persona'}
                  </TabsTrigger>
                );
              })}
            </TabsList>
            {selectedPersonas.map((personaId) => {
              const simulationId = personaSimulations[personaId];
              const failure = simulationId ? failures[simulationId] : undefined;
              return (
                <TabsContent
                  key={personaId}
                  value={personaId}
                  className="space-y-4"
                >
                  {failure && simulationId && !isSimulating && (
                    <div className="flex flex-col gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                      <p className="flex items-start gap-2">
                        <AlertTriangle className="mt-0.5 h-4 w-4 flex-none" />
                        <span>
                          {failure.reason} It can't be evaluated until it
                          finishes.
                          {failure.code === ENDPOINT_ERROR
                            ? // The endpoint may have acted on the message before
                              // failing, and only the customer knows what it does.
                              ' Your agent may already have received the last message. Continuing sends it again: if your agent saves data or sends messages, that could happen twice.'
                            : failure.code !== SPEND_LIMIT_REACHED &&
                              ' Continue the simulation to try again.'}
                        </span>
                      </p>
                      {failure.code === SPEND_LIMIT_REACHED &&
                        isImportedAgent && (
                          <RaiseSpendLimit
                            onRaise={(cents) =>
                              onRaiseLimitAndContinue(simulationId, cents)
                            }
                          />
                        )}
                    </div>
                  )}
                  <div
                    ref={(el) => {
                      conversationScrollRefs.current[personaId] = el;
                    }}
                    className="mb-4 max-h-[600px] min-h-[200px] space-y-4 overflow-y-auto"
                  >
                    {(personaMessages[personaId] || []).map(
                      (message, index) => (
                        <div
                          key={index}
                          className={`flex ${
                            message.role === 'user'
                              ? 'justify-end'
                              : 'justify-start'
                          }`}
                        >
                          <div
                            className={`flex max-w-[80%] items-start gap-2 ${
                              message.role === 'user'
                                ? 'flex-row-reverse'
                                : 'flex-row'
                            }`}
                          >
                            <div
                              className={`rounded-full p-2 ${
                                message.role === 'user'
                                  ? 'bg-prompt-safecomoutrageous-orange'
                                  : 'bg-prompt-safecomabbey'
                              }`}
                            >
                              {message.role === 'user' ? (
                                <User className="h-4 w-4 text-white" />
                              ) : (
                                <Bot className="h-4 w-4 text-white" />
                              )}
                            </div>
                            <div
                              className={`rounded-lg p-3 ${
                                message.role === 'user'
                                  ? 'bg-prompt-safecomoutrageous-orange text-white'
                                  : 'border border-[#00000020] bg-white'
                              }`}
                            >
                              {isImportedAgent &&
                                index === 0 &&
                                message.role === 'assistant' && (
                                  <OpeningMessageLabel />
                                )}
                              <Markdown
                                content={message.content}
                                className={
                                  message.role === 'user'
                                    ? 'prose-invert text-white'
                                    : ''
                                }
                              />
                            </div>
                          </div>
                        </div>
                      ),
                    )}
                    {isSimulating && typingRole[personaId] === 'assistant' && (
                      <div className="flex justify-start">
                        <div className="flex items-start gap-2">
                          <div className="rounded-full bg-prompt-safecomabbey p-2">
                            <Bot className="h-4 w-4 text-white" />
                          </div>
                          <div className="rounded-lg border border-[#00000020] bg-white p-3">
                            Typing...
                          </div>
                        </div>
                      </div>
                    )}
                    {isSimulating && typingRole[personaId] === 'user' && (
                      <div className="flex justify-end">
                        <div className="flex flex-row-reverse items-start gap-2">
                          <div className="rounded-full bg-prompt-safecomoutrageous-orange p-2">
                            <User className="h-4 w-4 text-white" />
                          </div>
                          <div className="rounded-lg bg-prompt-safecomoutrageous-orange p-3 text-white">
                            Typing...
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {canStartConversation && selectedPersonaTab === personaId && (
                    <div className="mt-auto flex gap-2">
                      <Input
                        value={userMessage}
                        onChange={(e) => setUserMessage(e.target.value)}
                        placeholder="Type your message..."
                        className="bg-white"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleSendMessage();
                          }
                        }}
                        disabled={isSendingMessage || isSimulating}
                      />
                      <Button
                        onClick={handleSendMessage}
                        disabled={
                          !userMessage.trim() ||
                          isSendingMessage ||
                          isSimulating
                        }
                        className="hover:bg-prompt-safecomoutrageous-orange/90 bg-prompt-safecomoutrageous-orange text-white"
                      >
                        <Send className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </TabsContent>
              );
            })}
          </Tabs>
        ) : (
          <div className="py-4 text-center text-prompt-safecomabbey">
            Select personas to start a conversation
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/** The first line is PromptSafe's, not the imported agent's. */
function OpeningMessageLabel() {
  return (
    <span className="mb-1 block text-xs text-prompt-safecomabbey">
      Opening message · not from your agent
    </span>
  );
}
